import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { AnalyticsNote, ExpandableDisclaimer } from "@/components/Compliance";
import usePageMeta from "@/hooks/usePageMeta";
import { ARTICLES, getArticle } from "@/data/insights";

export default function InsightArticle() {
  const { slug } = useParams();
  const article = getArticle(slug);
  const next = article ? ARTICLES[(ARTICLES.indexOf(article) + 1) % ARTICLES.length] : null;

  usePageMeta({
    title: article ? `${article.title} — Adviserz Insights` : "Insights — Adviserz",
    description: article?.summary,
    canonical: `https://adviserz.in/insights/${slug}`,
  });

  if (!article) return <Navigate to="/insights" replace />;

  return (
    <div className="min-h-screen bg-surface-base text-white">
      <SiteHeader active="insights" />

      <article className="relative">
        <div className="absolute inset-x-0 top-0 h-[420px] grid-bg-fine opacity-50 grid-radial-mask" />
        <div className="relative max-w-3xl mx-auto px-6 md:px-12 pt-32 md:pt-40 pb-20">
          <Link to="/insights" data-testid="back-to-insights" className="inline-flex items-center gap-2 text-xs text-zinc-500 hover:text-white transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> All insights
          </Link>

          <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-500">
            <span className="text-ai-accent">{article.category}</span>
            <span className="text-zinc-700">/</span>
            <span>{article.readTime}</span>
            <span className="text-zinc-700">/</span>
            <span>Educational</span>
          </div>

          <h1 data-testid="article-title" className="mt-5 font-display font-medium text-[32px] sm:text-5xl tracking-tighter leading-[1.06]">
            {article.title}
          </h1>
          <p className="mt-6 text-base md:text-lg text-zinc-400 leading-relaxed">{article.summary}</p>

          <div className="mt-8"><AnalyticsNote /></div>

          <div className="mt-14 space-y-12">
            {article.sections.map((s, i) => (
              <section key={s.h} data-testid={`article-section-${i}`}>
                <div className="flex items-baseline gap-4">
                  <span className="font-mono text-xs text-ai-accent">{String(i + 1).padStart(2, "0")}</span>
                  <h2 className="font-display font-medium text-xl md:text-2xl tracking-tight">{s.h}</h2>
                </div>
                <div className="mt-4 sm:pl-9 space-y-4">
                  {(s.p || []).map((para, j) => (
                    <p key={j} className="text-[15px] text-zinc-400 leading-[1.75]">{para}</p>
                  ))}
                  {s.list && (
                    <ul className="space-y-2.5 pt-1">
                      {s.list.map((li) => (
                        <li key={li} className="flex gap-3 text-[15px] text-zinc-400 leading-relaxed">
                          <span className="mt-2.5 w-1 h-1 rounded-full bg-ai-accent shrink-0" />
                          <span>{li}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </section>
            ))}
          </div>

          <div className="mt-16"><ExpandableDisclaimer testid="article-disclaimer" /></div>

          {next && (
            <Link to={`/insights/${next.slug}`} data-testid="next-article"
              className="mt-10 block rounded-lg border border-white/[0.08] bg-white/[0.02] hover:bg-white/[0.05] transition-colors p-6">
              <div className="label-xs text-zinc-500 mb-2">Next</div>
              <div className="flex items-center justify-between gap-4">
                <div className="font-display font-medium text-lg tracking-tight">{next.title}</div>
                <ArrowRight className="w-4 h-4 text-ai-accent shrink-0" />
              </div>
            </Link>
          )}
        </div>
      </article>

      <SiteFooter />
    </div>
  );
}
